// ─── widgets/menu.js ──────────────────────────────────────────────────────────
// Creates Menu and MenuContainer widget objects.
//
// Key behaviours:
//  • Elements with class="nav-logo" inside the menu-container are treated as
//    STANDALONE widgets (text or button) parented to the menu uid.
//    They are excluded from the menuContainerChildEls skip set so the main
//    extraction loop picks them up, and excluded from the MenuContainer
//    content array so they don't appear as nav links.
//  • All other non-hamburger children go into the MenuContainer content array.
//  • Hamburgers are skip-only (added to skip set, not to content).

import shortid from 'shortid';
import { cs, getAbsoluteRect } from '../utils/dom.js';
import { extractRGBAFromColor } from '../utils/style.js';
import { MENU, MENU_DRAGGABLE_CONTAINER } from 'constants/widgetTypes.jsx';
import { resolveFontFamily } from '../utils/fonts.js';

// ── Hamburger detector ────────────────────────────────────────────────────────
function isHamburger(el) {
    return (
        !!el.querySelector('svg') ||
        (el.tagName === 'BUTTON' && el.textContent.trim().length <= 3) ||
        /menu|open|close|toggle|nav/.test((el.getAttribute('aria-label') || '').toLowerCase())
    );
}

// ── Nav-logo detector ─────────────────────────────────────────────────────────
// Detects elements that carry class="nav-logo" on themselves or on their
// wrapping <a> ancestor. These should become standalone text/button widgets
// parented to the menu — not nav-link content items inside MenuContainer.
export function isNavLogo(el) {
    const targets = [el, el.closest('a')].filter(Boolean);
    return targets.some((t) => (t.className || '').toLowerCase().split(/\s+/).includes('nav-logo'));
}

// ─── createMenuWidgetObj ──────────────────────────────────────────────────────
/**
 * @param {{ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef }}
 * @returns {{ menuWidget, menuUid }}
 */
export function createMenuWidgetObj({ el, mobileEl, parentUid, offsetRect, mobileOffsetRect, orderRef }) {
    const rect  = getAbsoluteRect(el);
    const mRect = mobileEl
        ? getAbsoluteRect(mobileEl)
        : { width: 375, height: rect.height * 0.6, top: rect.top, left: 0 };

    const menuUid = `widget_${shortid.generate()}`;
    const ord     = orderRef.value++;
    const style   = cs(el);
    const bgc     = style.backgroundColor;
    const hasBg   = bgc && bgc !== 'rgba(0, 0, 0, 0)' && bgc !== 'transparent';

    const bg = hasBg
        ? { type: 'color', color: extractRGBAFromColor(bgc), media: null, opacity: 0, scale: null, position: null, blur: 0 }
        : { type: 'color', color: { type: 'solid', r: 0, g: 0, b: 0, a: 1 }, media: null, opacity: 0, scale: null, position: null, blur: 0 };

    const mOff = mobileOffsetRect || offsetRect;

    const menuWidget = {
        uid:           menuUid,
        type:          MENU,
        name:          'Menu',
        parent:        parentUid,
        order:         ord,
        systemDefined: true,
        fonts:         null,
        actions:       null,
        parameter:     null,
        pageId:        null,
        properties: {
            desktop: {
                visible:         true,
                disableOptimize: false,
                layout: {
                    transform: { translate: `0px,${Math.round(rect.top - (offsetRect.top || 0))}px`, rotate: '0deg', scale: '1,1' },
                    width:  '100%',
                    height: `${Math.round(rect.height)}px`,
                },
                locked:            false,
                stretched:         true,
                padding:           0,
                container:         { minWidth: 1200, alignment: 'center' },
                scrollEffect:      'NONE',
                background:        bg,
                alignment:         { position: 'center', margin: null },
                verticalAlignment: { position: 'center', margin: null },
            },
            mobile: {
                visible:         true,
                disableOptimize: false,
                layout: {
                    transform: { translate: `0px,${Math.round(mRect.top - (mOff.top || 0))}px`, rotate: '0deg' },
                    width:  '100%',
                    height: `${Math.round(mRect.height)}px`,
                },
                locked:            false,
                stretched:         false,
                padding:           0,
                container:         { minWidth: 375, alignment: null },
                scrollEffect:      null,
                background:        null,
                alignment:         { position: 'center', margin: null },
                verticalAlignment: { position: 'center', margin: null },
            },
        },
        content: null,
    };

    return { menuWidget, menuUid };
}

// ─── createMenuContainerWidgetObj ─────────────────────────────────────────────
/**
 * Builds the MenuContainer widget and populates `menuContainerChildEls`.
 *
 * Skip-set policy:
 *   • nav-logo elements (class="nav-logo") are EXCLUDED from the skip set so
 *     the main extraction loop creates them as standalone text/button widgets
 *     parented to the menu uid. They are also excluded from the content array.
 *   • All other non-logo, non-hamburger children are added to the skip set
 *     and appear in the content array as nav link items.
 *   • Hamburgers are added to the skip set only (not content).
 *
 * @param {{ el, mobileEl, menuUid, offsetRect, mobileOffsetRect, orderRef, menuWidget, menuContainerChildEls }}
 * @returns {Object} MenuContainer widget object
 */
export function createMenuContainerWidgetObj({
    el, mobileEl, menuUid, offsetRect, mobileOffsetRect, orderRef, menuWidget, menuContainerChildEls, fontFamilyMap, fontIdMap,
}) {
    const rect   = getAbsoluteRect(el);
    const mRect  = mobileEl
        ? getAbsoluteRect(mobileEl)
        : { width: 40, height: 30, top: rect.top + 10, left: 167 };

    const menuH  = offsetRect.height  || rect.height;
    const mMenuH = mobileOffsetRect?.height || mRect.height;
    const y      = Math.round((menuH  - 37) / 2);
    const mY     = Math.round((mMenuH - 30) / 2);

    const bgS = { type: 'solid', r: 0,   g: 0,   b: 0,   a: 1 };
    const wS  = { type: 'solid', r: 255, g: 255, b: 255, a: 1 };
    const zB  = {
        sides: null, color: wS, width: '0',
        radius: { lock: false, topLeft: '0', topRight: '0', bottomLeft: '0', bottomRight: '0' },
    };

    const allInner = Array.from(el.querySelectorAll('[data-widget="button"], [data-widget="text"]'));

    // ── Populate skip set ─────────────────────────────────────────────────────
    // nav-logo elements are intentionally left OUT of the skip set so the main
    // loop can create them as real text/button widgets under the menu uid.
    allInner.forEach((c) => {
        if (!isNavLogo(c)) menuContainerChildEls.add(c);
    });
    // Bare <button> hamburgers
    Array.from(el.querySelectorAll('button')).forEach((c) => {
        if (isHamburger(c)) menuContainerChildEls.add(c);
    });

    // ── Build nav content items ───────────────────────────────────────────────
    // Exclude hamburgers AND nav-logo elements — logos become standalone widgets,
    // not nav-link items inside the MenuContainer content array.
    const seen    = new Set();
    const content = allInner
        .filter((c) => !isHamburger(c) && !isNavLogo(c))
        .reduce((acc, c) => {
            const text = c.textContent.trim();
            if (!text || seen.has(text)) return acc;
            seen.add(text);
            const anchor = c.tagName === 'A' ? c : c.closest('a');
            const href   = anchor ? anchor.getAttribute('href') : null;
            acc.push({
                uid:      shortid.generate(),
                name:     text,
                // action:   href
                //     ? { type: 'link', value: href, newTab: anchor.getAttribute('target') === '_blank' }
                //     : null,
                children: null,
            });
            return acc;
        }, []);

    // Nav link colour (first non-hamburger element's computed color)
    const firstNav = allInner.find((c) => !isHamburger(c) && !isNavLogo(c));
    const filteredNav = allInner.filter((el) => !isHamburger(el) && !isNavLogo(el))
    let firstRect = filteredNav[0]?.getBoundingClientRect();
    let lastRect = filteredNav[filteredNav.length - 1]?.getBoundingClientRect();
    let width = (lastRect.x - firstRect.x) + lastRect.width;
    let x = firstRect?.x || 472.5;
    const styles = cs(firstNav || el);
    const { resolvedFamily, fontIds } = resolveFontFamily(styles.fontFamily, fontFamilyMap, fontIdMap);
    const fontFamily = resolvedFamily || styles.fontFamily;
    const navColor = firstNav ? (extractRGBAFromColor(styles.color) || wS) : wS;
    const mkMS     = (fc) => ({
        font: {
            style:         { bold: false, italic: false, underline: false },
            fontSize:      styles.fontSize.toString(),
            fontFamily:    fontFamily,
            lineSpacing:   null,
            letterSpacing: null,
            color:         fc,
        },
        background: null,
        border:     zB,
        shadow:     null,
    });

    return {
        uid:           `widget_${shortid.generate()}`,
        type:          MENU_DRAGGABLE_CONTAINER,
        name:          'MenuContainer',
        parent:        menuUid,
        order:         orderRef.value++,
        systemDefined: true,
        fonts:         null,
        actions:       null,
        parameter:     null,
        pageId:        null,
        properties: {
            desktop: {
                visible:         true,
                disableOptimize: false,
                layout: {
                    transform: { translate: `${x}px,${y}px`, rotate: '0deg' },
                    width:  `${width}px`,
                    height: 'auto',
                },
                locked:              false,
                regular:             { menuStyles: mkMS(navColor), dropdownColor: wS, dropdownBackground: bgS, dropdownBorder: zB },
                hover:               { menuStyles: mkMS({ type: 'solid', r: 0, g: 0, b: 0, a: 1 }), dropdownColor: wS, dropdownBackground: bgS, dropdownBorder: zB },
                alignment:           { position: 'center', margin: null },
                trayAlignment:       { position: 'center', margin: null },
                padding:             0,
                itemHorizontalPadding: 8,
                itemVerticalPadding:   8,
                trayBackground:      wS,
                animation:           null,
            },
            mobile: {
                visible:         true,
                disableOptimize: false,
                layout: {
                    transform: { translate: `325px,${mY}px`, rotate: '0deg' },
                    width:  '40px',
                    height: '30px',
                },
                locked:              false,
                regular:             { menuStyles: mkMS(navColor), dropdownColor: wS, dropdownBackground: bgS, dropdownBorder: zB },
                hover:               { menuStyles: mkMS({ type: 'solid', r: 0, g: 0, b: 0, a: 1 }), dropdownColor: wS, dropdownBackground: bgS, dropdownBorder: zB },
                alignment:           { position: 'center', margin: null },
                trayAlignment:       { position: 'center', margin: null },
                padding:             0,
                itemHorizontalPadding: 0,
                itemVerticalPadding:   0,
                trayBackground:      wS,
                animation:           null,
            },
        },
        content,
    };
}